/**
 * Utility functions for converting between metric and imperial units
 * Used throughout the application based on user's unit preference
 */

export type UnitSystem = 'metric' | 'imperial';

// Speed conversion functions
export const convertSpeed = {
  /**
   * Convert km/h to mph
   */
  kmhToMph: (kmh: number): number => {
    return Math.round(kmh * 0.621371 * 100) / 100; // Round to 2 decimals
  },

  /**
   * Convert mph to km/h
   */
  mphToKmh: (mph: number): number => {
    return Math.round(mph * 1.609344 * 100) / 100; // Round to 2 decimals
  },

  /**
   * Convert speed value based on target unit system
   */
  convert: (value: number, from: UnitSystem, to: UnitSystem): number => {
    if (from === to) return value;

    if (from === 'metric' && to === 'imperial') {
      return convertSpeed.kmhToMph(value);
    } else if (from === 'imperial' && to === 'metric') {
      return convertSpeed.mphToKmh(value);
    }

    return value;
  },
};

// Weight conversion functions
export const convertWeight = {
  /**
   * Convert kg to lbs
   */
  kgToLbs: (kg: number): number => {
    return Math.round(kg * 2.20462 * 100) / 100; // Round to 2 decimals
  },

  /**
   * Convert lbs to kg
   */
  lbsToKg: (lbs: number): number => {
    return Math.round((lbs / 2.20462) * 100) / 100; // Round to 2 decimals
  },

  /**
   * Convert weight value based on target unit system
   */
  convert: (value: number, from: UnitSystem, to: UnitSystem): number => {
    if (from === to) return value;

    if (from === 'metric' && to === 'imperial') {
      return convertWeight.kgToLbs(value);
    } else if (from === 'imperial' && to === 'metric') {
      return convertWeight.lbsToKg(value);
    }

    return value;
  },
};

// Height conversion functions
export const convertHeight = {
  /**
   * Convert cm to feet and inches
   * Returns object with feet and inches
   */
  cmToFeetInches: (cm: number): { feet: number; inches: number } => {
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return { feet, inches };
  },

  /**
   * Convert feet and inches to cm
   */
  feetInchesToCm: (feet: number, inches: number): number => {
    return Math.round((feet * 12 + inches) * 2.54);
  },

  /**
   * Convert cm to total inches
   */
  cmToInches: (cm: number): number => {
    return Math.round((cm / 2.54) * 100) / 100; // Round to 2 decimals
  },

  /**
   * Convert inches to cm
   */
  inchesToCm: (inches: number): number => {
    return Math.round(inches * 2.54 * 100) / 100; // Round to 2 decimals
  },

  /**
   * Convert height value based on target unit system
   * For imperial, returns total inches (can be converted to feet/inches later)
   */
  convert: (value: number, from: UnitSystem, to: UnitSystem): number => {
    if (from === to) return value;

    if (from === 'metric' && to === 'imperial') {
      return convertHeight.cmToInches(value);
    } else if (from === 'imperial' && to === 'metric') {
      return convertHeight.inchesToCm(value);
    }

    return value;
  },
};

// Temperature conversion functions (for future use)
export const convertTemperature = {
  /**
   * Convert Celsius to Fahrenheit
   */
  celsiusToFahrenheit: (celsius: number): number => {
    return Math.round(((celsius * 9) / 5 + 32) * 10) / 10; // Round to 1 decimal
  },

  /**
   * Convert Fahrenheit to Celsius
   */
  fahrenheitToCelsius: (fahrenheit: number): number => {
    return Math.round((((fahrenheit - 32) * 5) / 9) * 10) / 10; // Round to 1 decimal
  },

  /**
   * Convert temperature value based on target unit system
   */
  convert: (value: number, from: UnitSystem, to: UnitSystem): number => {
    if (from === to) return value;

    if (from === 'metric' && to === 'imperial') {
      return convertTemperature.celsiusToFahrenheit(value);
    } else if (from === 'imperial' && to === 'metric') {
      return convertTemperature.fahrenheitToCelsius(value);
    }

    return value;
  },
};

// Unit label functions
export const getUnitLabels = {
  speed: (unitSystem: UnitSystem): string => {
    return unitSystem === 'metric' ? 'km/h' : 'mph';
  },

  weight: (unitSystem: UnitSystem): string => {
    return unitSystem === 'metric' ? 'kg' : 'lbs';
  },

  height: (unitSystem: UnitSystem): string => {
    return unitSystem === 'metric' ? 'cm' : 'ft/in';
  },

  temperature: (unitSystem: UnitSystem): string => {
    return unitSystem === 'metric' ? '°C' : '°F';
  },
};

// Format functions for display
export const formatValue = {
  /**
   * Format speed value with appropriate unit
   */
  speed: (value: number, unitSystem: UnitSystem): string => {
    const unit = getUnitLabels.speed(unitSystem);
    return `${value.toFixed(2)} ${unit}`;
  },

  /**
   * Format weight value with appropriate unit
   */
  weight: (value: number, unitSystem: UnitSystem): string => {
    const unit = getUnitLabels.weight(unitSystem);
    return `${value.toFixed(2)} ${unit}`;
  },

  /**
   * Format height value with appropriate unit
   */
  height: (value: number, unitSystem: UnitSystem): string => {
    if (unitSystem === 'metric') {
      return `${value.toFixed(2)} cm`;
    } else {
      const { feet, inches } = convertHeight.cmToFeetInches(value);
      return `${feet}'${inches}"`;
    }
  },

  /**
   * Format temperature value with appropriate unit
   */
  temperature: (value: number, unitSystem: UnitSystem): string => {
    const unit = getUnitLabels.temperature(unitSystem);
    return `${value.toFixed(2)}${unit}`;
  },
};

// Main conversion function that handles all unit types
export const convertValue = (
  value: number,
  unitType: 'speed' | 'weight' | 'height' | 'temperature',
  from: UnitSystem,
  to: UnitSystem
): number => {
  switch (unitType) {
    case 'speed':
      return convertSpeed.convert(value, from, to);
    case 'weight':
      return convertWeight.convert(value, from, to);
    case 'height':
      return convertHeight.convert(value, from, to);
    case 'temperature':
      return convertTemperature.convert(value, from, to);
    default:
      return value;
  }
};

// Helper function to get display value based on user's unit preference
export const getDisplayValue = (
  value: number,
  unitType: 'speed' | 'weight' | 'height' | 'temperature',
  userUnits: UnitSystem,
  sourceUnits: UnitSystem = 'metric'
): { value: number; unit: string; formatted: string } => {
  const convertedValue = convertValue(value, unitType, sourceUnits, userUnits);
  const unit = getUnitLabels[unitType](userUnits);
  const formatted = formatValue[unitType](convertedValue, userUnits);

  return {
    value: convertedValue,
    unit,
    formatted,
  };
};

// Helper function to scale distance data with unit conversion
export const scaleDataWithUnits = (
  value: number | undefined,
  userUnits: UnitSystem,
  sourceUnits: UnitSystem = 'metric'
): string => {
  if (value === undefined) {
    const unit = userUnits === 'metric' ? 'm' : 'ft';
    return `?? ${unit}`;
  }

  let convertedValue = value;
  let suffix = 'm';

  // Convert to user's preferred units if needed
  if (sourceUnits !== userUnits) {
    if (sourceUnits === 'metric' && userUnits === 'imperial') {
      // Convert meters to feet
      convertedValue = value * 3.28084;
      suffix = 'ft';
    } else if (sourceUnits === 'imperial' && userUnits === 'metric') {
      // Convert feet to meters
      convertedValue = value / 3.28084;
      suffix = 'm';
    }
  } else {
    suffix = userUnits === 'metric' ? 'm' : 'ft';
  }

  // Handle small values in metric system (convert to cm)
  if (userUnits === 'metric' && convertedValue < 1 && suffix === 'm') {
    suffix = 'cm';
    convertedValue *= 100;
  }
  // Handle small values in imperial system (convert to inches)
  else if (userUnits === 'imperial' && convertedValue < 1 && suffix === 'ft') {
    suffix = 'in';
    convertedValue *= 12;
  }

  return `${convertedValue.toFixed(2)} ${suffix}`;
};
